import * as React from 'react'

import {
  Box,
  Button,
  EnhancedDimension,
  type LabeledInputProps,
  Text,
} from '@factorialco/deprecated-design-system'
import type { Field } from 'factorial-form'

import { observer } from 'mobx-react'

import EmployeeSelector from 'modules/core/components/EmployeeSelector'
import type { FilterProps } from 'modules/core/components/Filters/types'
import HelpCenterLink from 'modules/core/components/HelpCenterLink'
import EmployeeTextField from 'modules/core/components/Inputs/EmployeeSelector/TextField'
import Popover from 'modules/core/components/Popover'
import type { Attachment } from 'modules/core/components/TetherComponent/types'
import TextField from 'modules/core/components/TextField'
import { useEffect } from 'modules/core/hooks/useEffect'
import { HelpCenterUrl } from 'modules/core/lib/helpCenterUrl'
import i18n from 'modules/core/lib/i18n'
import { employeeUser } from 'modules/core/store/collections/users'

import type Employee from 'modules/employees/store/models/Employee'

export interface FooterInfo {
  description: string
  helpLinkText?: string
  helpLinkName?: HelpCenterUrl
}

type onSelectEmployee = (employeeId: number | null | undefined) => void
type onSelectMultipleEmployees = (employeeId: number[] | number | null | undefined) => void

type Props = LabeledInputProps & {
  label: string
  attachment: Attachment
  employees: Employee[]
  field: Field
  footerInfo?: FooterInfo | null | undefined
  filterProps?: FilterProps
  multiFilters?: boolean
  selectedEmployeeIds?: number[]
  hasSubmitButton?: boolean
  isMultiSelect?: boolean
  onSelectEmployee?: onSelectEmployee | onSelectMultipleEmployees
  disabledEmployeeIds?: number[]
  getDisabledEmployeeReason?: (employee: Employee) => string | undefined
  virtualized?: boolean
  width?: EnhancedDimension | null
}

interface FooterProps {
  footerInfo: FooterInfo | null | undefined
}

const Footer = ({ footerInfo }: FooterProps) => {
  if (!footerInfo) return null

  return (
    <Box justifyContent="center" alignItems="center">
      <Text.Normal size="300">{footerInfo.description}</Text.Normal>
      {footerInfo.helpLinkText && footerInfo.helpLinkName && (
        <HelpCenterLink name={footerInfo.helpLinkName}>{footerInfo.helpLinkText}</HelpCenterLink>
      )}
    </Box>
  )
}

interface SubmitButtonProps {
  hasSubmitButton: boolean
  selectedEmployeeIds: number[]
  onSubmit: () => void
}

const SubmitButton = ({ hasSubmitButton, selectedEmployeeIds, onSubmit }: SubmitButtonProps) => {
  if (!hasSubmitButton) return null

  const usersSelected = i18n.tp('user_selector.submit', {
    count: selectedEmployeeIds.length,
  })

  const noUsersSelected = i18n.t('user_selector.submit.zero')
  const label = selectedEmployeeIds.length ? usersSelected : noUsersSelected

  return (
    <Box paddingTop="s24">
      <Button onClick={onSubmit}>{label}</Button>
    </Box>
  )
}

interface CommonContentProps {
  employees: Employee[]
  selectedEmployeeIds: number[]
  filterProps?: FilterProps
  multiFilters?: boolean
  hasSubmitButton: boolean
  footerInfo?: FooterInfo | null | undefined
  setChildPopoverOpen: (isOpen: boolean) => void
  onSubmit: () => void
  selectedEmployeeId: number | null | undefined
  disabledEmployeeIds: number[]
  getDisabledEmployeeReason?: (employee: Employee) => string | undefined
  virtualized?: boolean
}
interface SingleSelectProps extends CommonContentProps {
  isMultiSelect?: boolean
  onSelectEmployee?: onSelectEmployee
}

interface MultipleSelectProps extends CommonContentProps {
  isMultiSelect: true
  onSelectEmployee?: onSelectMultipleEmployees
}

type ContentProps = SingleSelectProps | MultipleSelectProps

function isMultiSelectProps(props: ContentProps): props is MultipleSelectProps {
  return Boolean(props.isMultiSelect)
}

const Content = (props: ContentProps) => {
  if (isMultiSelectProps(props)) {
    return (
      <Box padding="s32" gap="s24">
        <EmployeeSelector
          employees={props.employees}
          filterProps={props.filterProps}
          onSelectEmployee={props.onSelectEmployee}
          onSelectMultiple={props.onSelectEmployee}
          selectedEmployeeIds={props.selectedEmployeeIds}
          multiFilters={props.multiFilters}
          setChildPopoverOpen={props.setChildPopoverOpen}
          disabledEmployeeIds={props.disabledEmployeeIds}
          getDisabledEmployeeReason={props.getDisabledEmployeeReason}
          virtualized={props.virtualized}
        />
        <SubmitButton
          hasSubmitButton={props.hasSubmitButton}
          selectedEmployeeIds={props.selectedEmployeeIds}
          onSubmit={props.onSubmit}
        />
        <Footer footerInfo={props.footerInfo} />
      </Box>
    )
  }

  return (
    <Box padding="s32" gap="s24">
      <EmployeeSelector
        employees={props.employees}
        onSelectEmployee={props.onSelectEmployee}
        selectedEmployeeIds={props.selectedEmployeeId ? [props.selectedEmployeeId] : []}
        filterProps={props.filterProps}
        multiFilters={props.multiFilters}
        setChildPopoverOpen={props.setChildPopoverOpen}
        disabledEmployeeIds={props.disabledEmployeeIds}
        getDisabledEmployeeReason={props.getDisabledEmployeeReason}
      />
      <Footer footerInfo={props.footerInfo} />
    </Box>
  )
}

const EmployeeSelectorInput = ({
  readonly,
  attachment,
  label,
  secondaryLabel,
  field,
  filterProps,
  employees,
  footerInfo,
  onSelectEmployee,
  multiFilters = false,
  isMultiSelect = false,
  selectedEmployeeIds = [],
  hasSubmitButton = false,
  disabledEmployeeIds = [],
  getDisabledEmployeeReason,
  mandatory,
  virtualized = true,
  width = null,
}: Props) => {
  const [isFocused, setIsFocused] = React.useState(false)
  const [childPopoverOpen, setChildPopoverOpen] = React.useState(false)
  const clickedInside = React.useRef(false)
  const clickTimeout = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const fieldValue = field.value
  const selectedEmployeeId = parseInt(fieldValue)

  useEffect(() => {
    return () => {
      if (clickTimeout.current) {
        clearTimeout(clickTimeout.current)
      }
    }
  }, [])

  function toggleFocus(value: boolean) {
    return () => {
      setIsFocused(value)
    }
  }

  function employeeFullName(): string | null | undefined {
    const fieldValue = field.value

    if (!fieldValue) return null

    const employee = employees.find((employee) => employee.attributes.id === parseInt(fieldValue))

    if (!employee) return null

    return employeeUser(employee).fullName()
  }

  if (readonly) {
    return (
      <TextField
        readonly
        label={label}
        secondaryLabel={secondaryLabel}
        value={employeeFullName() || i18n.t('common.none')}
      />
    )
  }

  function renderTextField() {
    return (
      <EmployeeTextField
        selectedEmployeeIds={selectedEmployeeIds}
        isMultiSelect={isMultiSelect}
        label={label}
        field={field}
        onFocus={toggleFocus(true)}
        onBlur={onBlur}
        secondaryLabel={secondaryLabel}
        mandatory={mandatory}
        employees={employees}
      />
    )
  }

  function onBlur() {
    setIsFocused(clickedInside.current)
  }

  function onSubmit() {
    setIsFocused(false)
  }

  function onHandleContainerMouseDown() {
    clickedInside.current = true
    // The input's onBlur method is called from a queue right after onMouseDown event.
    // setTimeout adds another callback in the queue, but is called later than onBlur event
    clickTimeout.current = setTimeout(() => {
      clickedInside.current = false
    }, 0)
  }

  function onSelectEmployeeCallback(employeeId: number | null | undefined) {
    if (!employeeId) {
      field.set(null)
    } else {
      if (selectedEmployeeId === employeeId) {
        field.set(null)
      } else {
        field.set(employeeId)
      }

      if (onSelectEmployee) {
        onSelectEmployee(employeeId)
      }
    }

    // This is ugly as hell but it makes for a good user experience
    if (!isMultiSelect) {
      setTimeout(() => {
        setIsFocused(false)
      }, 150)
    }
  }

  if (!isFocused) return renderTextField()

  return (
    <Popover
      attachment={attachment}
      open={isFocused}
      size={width ? 'auto' : 'bigger'}
      toggle={toggleFocus}
      disableOutsideClick={childPopoverOpen}
    >
      {renderTextField()}
      <Box width={width || 'full'} onMouseDown={onHandleContainerMouseDown}>
        <Content
          employees={employees}
          onSelectEmployee={onSelectEmployeeCallback}
          selectedEmployeeIds={selectedEmployeeIds}
          isMultiSelect={isMultiSelect}
          filterProps={filterProps}
          multiFilters={multiFilters}
          setChildPopoverOpen={setChildPopoverOpen}
          hasSubmitButton={hasSubmitButton}
          selectedEmployeeId={selectedEmployeeId}
          onSubmit={onSubmit}
          footerInfo={footerInfo}
          disabledEmployeeIds={disabledEmployeeIds}
          getDisabledEmployeeReason={getDisabledEmployeeReason}
          virtualized={virtualized}
        />
      </Box>
    </Popover>
  )
}

export default observer(EmployeeSelectorInput)
